import {
  Box,
  Card,
  CardContent,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
  alpha,
} from "@mui/material";
import { Check, Moon, RotateCcw, Sun } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CARD_STYLE_PRESETS } from "../../constants/cardStylePresets";
import { COLORS } from "../../constants/colors";
import { SPACING } from "../../constants/spacing";
import { useSettings } from "../../contexts/SettingsContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useEffectiveTheme } from "../../hooks/useEffectiveTheme";
import Button from "../Button";

const PRESET_COLORS = [
  { name: "Blue", value: COLORS.BLUE[500] },
  { name: "Green", value: COLORS.GREEN[500] },
  { name: "Orange", value: COLORS.ORANGE[500] },
  { name: "Red", value: COLORS.RED[500] },
  { name: "Purple", value: "#8B5CF6" },
  { name: "Pink", value: "#EC4899" },
  { name: "Teal", value: "#14B8A6" },
  { name: "Indigo", value: "#6366F1" },
];

const ThemeSettings = () => {
  const { t } = useTranslation(["settings", "common"]);
  const { theme, changeTheme } = useTheme();
  const { customPrimaryColor, setCustomPrimaryColor, cardStyle, setCardStyle } =
    useSettings();
  const { defaultPrimaryColor } = useEffectiveTheme();
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customColorValue, setCustomColorValue] = useState(
    customPrimaryColor || defaultPrimaryColor,
  );

  const handleThemeChange = (event) => {
    changeTheme(event.target.value);
  };

  const handlePresetColorSelect = (color) => {
    setCustomPrimaryColor(color);
    setShowCustomPicker(false);
  };

  const handleCustomColorClick = () => {
    setShowCustomPicker(true);
  };

  const handleCustomColorChange = (event) => {
    const newColor = event.target.value;
    setCustomColorValue(newColor);
    setCustomPrimaryColor(newColor);
  };

  const resetToDefault = () => {
    setCustomPrimaryColor(null);
    setCustomColorValue(defaultPrimaryColor);
    setShowCustomPicker(false);
  };

  const currentColor = customPrimaryColor || defaultPrimaryColor;
  const isPresetColor = PRESET_COLORS.some((pc) => pc.value === currentColor);
  const isCustomColor =
    !isPresetColor &&
    customPrimaryColor &&
    customPrimaryColor !== defaultPrimaryColor;

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: SPACING.sm }}>
        {t("appearance.title")}
      </Typography>

      <Typography
        variant="bodyMd"
        component="p"
        sx={{ color: "text.secondary", pb: SPACING.lg }}
      >
        {t("appearance.description")}
      </Typography>

      {/* Theme Setting */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 3,
        }}
      >
        <Box>
          <Typography
            variant="bodyMd"
            sx={{ fontWeight: 500, mb: 0.5, display: "block" }}
          >
            {t("appearance.theme")}
          </Typography>
          <Typography
            variant="bodyMd"
            sx={{ color: "text.secondary", display: "block" }}
          >
            {t("appearance.themeDescription")}
          </Typography>
        </Box>
        <Box sx={{ width: 250 }}>
          <FormControl fullWidth size="small">
            <Select
              value={theme || "light"}
              onChange={handleThemeChange}
              sx={{
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 1.5,
                },
              }}
            >
              <MenuItem value="light">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Sun size={18} color="#f59e0b" />
                  <Typography variant="bodySm">
                    {t("appearance.light")}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="dark">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Moon size={18} color="#6366f1" />
                  <Typography variant="bodySm">
                    {t("appearance.dark")}
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Primary Color Setting */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          py: 3,
          mt: SPACING.sm,
        }}
      >
        <Box>
          <Typography
            variant="bodyMd"
            sx={{ fontWeight: 500, mb: 0.5, display: "block" }}
          >
            {t("appearance.primaryColor")}
          </Typography>
          <Typography
            variant="bodyMd"
            sx={{ color: "text.secondary", display: "block" }}
          >
            {t("appearance.primaryColorDescription")}
          </Typography>
        </Box>
        <Box sx={{ width: 250 }}>
          {/* Color Palette */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              mb: SPACING.sm,
            }}
          >
            {PRESET_COLORS.map((color) => (
              <Paper
                key={color.value}
                onClick={() => handlePresetColorSelect(color.value)}
                elevation={0}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: color.value,
                  cursor: "pointer",
                  border:
                    currentColor === color.value
                      ? `3px solid ${alpha(color.value, 0.4)}`
                      : "3px solid transparent",
                  transition: "all 0.2s ease",
                }}
                title={color.name}
              />
            ))}

            {/* Custom Color Circle */}
            <Paper
              onClick={handleCustomColorClick}
              elevation={0}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: isCustomColor
                  ? customColorValue
                  : `conic-gradient(
                    red,
                    yellow,
                    lime,
                    aqua,
                    blue,
                    magenta,
                    red
                  )`,
                cursor: "pointer",
                border:
                  isCustomColor || showCustomPicker
                    ? `3px solid ${alpha(customColorValue, 0.4)}`
                    : "3px solid transparent",
                boxShadow:
                  isCustomColor || showCustomPicker
                    ? `0 0 0 2px ${theme === "dark" ? "#1e1e1e" : "#ffffff"}, 0 0 0 4px ${customColorValue}`
                    : "0 2px 4px rgba(0,0,0,0.1)",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  transform: "scale(1.1)",
                  boxShadow: `0 4px 8px rgba(0,0,0,0.2)`,
                },
              }}
              title="Custom Color"
            >
              {isCustomColor && (
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: customColorValue,
                    }}
                  >
                    ✓
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>

          {/* Custom Color Picker */}
          {showCustomPicker && (
            <Box
              sx={{
                mb: SPACING.sm,
                p: SPACING.sm,
                borderRadius: 1,
                backgroundColor: alpha(
                  theme === "dark" ? "#ffffff" : "#000000",
                  0.03,
                ),
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <input
                  type="color"
                  value={customColorValue}
                  onChange={handleCustomColorChange}
                  style={{
                    width: "60px",
                    height: "40px",
                    border: "none",
                    cursor: "pointer",
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="bodySm">
                    {customColorValue.toUpperCase()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Reset Button */}
          {customPrimaryColor && customPrimaryColor !== defaultPrimaryColor && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<RotateCcw size={16} />}
              onClick={resetToDefault}
              fullWidth
              sx={{ mt: 1 }}
            >
              {t("appearance.resetToDefault")}
            </Button>
          )}
        </Box>
      </Box>

      {/* Card Style Setting */}
      <Box sx={{ py: 3, mt: SPACING.sm }}>
        <Typography
          variant="bodyMd"
          sx={{ fontWeight: 500, mb: 0.5, display: "block" }}
        >
          {t("appearance.cardStyle")}
        </Typography>
        <Typography
          variant="bodyMd"
          sx={{ color: "text.secondary", display: "block", mb: SPACING.md }}
        >
          {t("appearance.cardStyleDescription")}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: 2.5,
          }}
        >
          {Object.values(CARD_STYLE_PRESETS).map((preset) => {
            const isSelected = cardStyle === preset.key;
            const isDark = theme === "dark";
            const modeBg =
              typeof preset.card.backgroundColor === "object"
                ? isDark
                  ? preset.card.backgroundColor.dark
                  : preset.card.backgroundColor.light
                : preset.card.backgroundColor;
            const modeShadow =
              typeof preset.card.boxShadow === "object"
                ? isDark
                  ? preset.card.boxShadow.dark
                  : preset.card.boxShadow.light
                : preset.card.boxShadow;
            const modeChartBg =
              typeof preset.chartArea?.backgroundColor === "object"
                ? isDark
                  ? preset.chartArea.backgroundColor.dark
                  : preset.chartArea.backgroundColor.light
                : preset.chartArea?.backgroundColor || "transparent";

            return (
              <Card
                key={preset.key}
                onClick={() => setCardStyle(preset.key)}
                sx={{
                  cursor: "pointer",
                  position: "relative",
                  border: "2px solid",
                  borderColor: isSelected ? "primary.main" : "divider",
                  borderRadius: "14px",
                  transition: "all 0.2s ease",
                  backgroundColor: "background.paper",
                  backgroundImage: "none",
                  backdropFilter: "none",
                  boxShadow: isSelected
                    ? (thm) =>
                        `0 0 0 1px ${thm.palette.primary.main}, 0 2px 8px ${alpha(thm.palette.primary.main, 0.2)}`
                    : "none",
                  "&:hover": {
                    borderColor: isSelected ? "primary.main" : "text.secondary",
                    transform: "translateY(-2px)",
                    boxShadow: isSelected
                      ? undefined
                      : "0 4px 12px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                  {isSelected && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 2,
                      }}
                    >
                      <Check size={13} color="white" />
                    </Box>
                  )}

                  {/* Live Preview Area */}
                  <Box
                    sx={{
                      p: 1.5,
                      m: 1.5,
                      mb: 0,
                      borderRadius: preset.card.borderRadius || "8px",
                      backgroundColor: modeBg || "transparent",
                      border: `${preset.card.borderWidth || "1px"} ${preset.card.borderStyle || "solid"}`,
                      borderColor: "divider",
                      boxShadow: modeShadow || "none",
                      backdropFilter: preset.card.backdropFilter || "none",
                      minHeight: 72,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                    }}
                  >
                    {/* Mini header */}
                    <Box
                      sx={{
                        width: "60%",
                        height: 6,
                        borderRadius: 1,
                        backgroundColor: "text.secondary",
                        opacity: 0.3,
                      }}
                    />
                    {/* Mini number */}
                    <Box
                      sx={{
                        width: "35%",
                        height: 10,
                        borderRadius: 1,
                        backgroundColor: "text.primary",
                        opacity: 0.5,
                      }}
                    />
                    {/* Mini chart area */}
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 28,
                        borderRadius: "4px",
                        backgroundColor: modeChartBg,
                        display: "flex",
                        alignItems: "flex-end",
                        gap: "3px",
                        p: 0.5,
                        pt: 0,
                      }}
                    >
                      {[35, 55, 40, 70, 50, 65, 45].map((h, i) => (
                        <Box
                          key={i}
                          sx={{
                            flex: 1,
                            height: `${h}%`,
                            borderRadius: "2px",
                            backgroundColor: "primary.main",
                            opacity: 0.6,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Label */}
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, lineHeight: 1.2 }}
                    >
                      {t(preset.label, { ns: "common" })}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.3,
                        display: "block",
                        mt: 0.25,
                      }}
                    >
                      {t(preset.description, { ns: "common" })}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default ThemeSettings;
